import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Copy,
  Check,
  MessageCircle,
  LogOut,
  Loader2,
  ArrowUpRight,
  Download,
  Landmark,
  Share2,
  Gift,
  UserCircle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FxSavingsWidget from "@/components/FxSavingsWidget";
import FloLogo from "@/components/FloLogo";
import KybStatusBanner from "@/components/KybStatusBanner";
import RbiBadge from "@/components/RbiBadge";
import { DASHBOARD } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
const usd = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground">
        <FloLogo variant="lockup-light" size={44} />
        <div className="flex items-center text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading dashboard…
        </div>
      </div>
    );
  }

  const { user: u, virtual_usd_account: va, payout, transactions } = data;

  const payoutStatus = u.payout_status || "pending";
  const payoutBadge =
    payoutStatus === "linked"
      ? { label: "Payout account: linked", cls: "border-[#1E8A5B]/30 text-[#1E8A5B] bg-[#E5F4EC]" }
      : payoutStatus === "error"
        ? { label: "Payout account: needs review", cls: "border-destructive/30 text-destructive bg-destructive/10" }
        : { label: "Payout account: pending", cls: "border-[#C77F1E]/30 text-[#8F5A16] bg-[#FBF1E1]" };

  const copyAll = async () => {
    const block =
      `Beneficiary: ${va.beneficiary_name}\n` +
      `Account number: ${va.account_number}\n` +
      `Routing (ACH / ABA): ${va.routing_number}\n` +
      `SWIFT / BIC: ${va.swift_bic}\n` +
      `Bank: ${va.bank_name}\n` +
      `Bank address: ${va.bank_address}\n` +
      `Account type: ${va.account_type}`;
    try {
      await navigator.clipboard.writeText(block);
      setCopiedAll(true);
      toast.success("All USD account details copied");
      setTimeout(() => setCopiedAll(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${BACKEND_URL}/api/account/usd/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Flo-USD-Collection-Details.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.message || "Could not download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid={DASHBOARD.root}>
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-4 md:p-6">
          <div className="flex items-center gap-3">
            <FloLogo variant="lockup-light" size={36} />
            {data?.user?.kyb_status === "activated" ? (
              <span className="inline-flex items-center gap-2 ml-1 px-3 py-1 rounded-full text-xs font-medium border border-[#1E8A5B]/30 text-[#1E8A5B] bg-[#E5F4EC]">
                <span className="h-2 w-2 rounded-full bg-success" />
                Account: active
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 ml-1 px-3 py-1 rounded-full text-xs font-medium border border-[#C77F1E]/30 text-[#8F5A16] bg-[#FBF1E1]">
                <span className="h-2 w-2 rounded-full bg-[#C77F1E]" />
                Account: pending verification
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to="/account">
              <Button variant="ghost" className="text-sm" data-testid="account-link">
                <UserCircle className="h-4 w-4 mr-2" /> My account
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" className="text-sm" data-testid={DASHBOARD.contactLink}>
                <MessageCircle className="h-4 w-4 mr-2" /> Support
              </Button>
            </Link>
            <Button variant="outline" onClick={logout} className="text-sm" data-testid={DASHBOARD.logoutBtn}>
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8 space-y-8">
        <KybStatusBanner status={u.kyb_status} />
        <RbiBadge />

        {/* Greeting */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Welcome back</p>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">
              {u.legal_name || u.email}
            </h1>
          </div>
        </div>

        {/* Top grid: USD account + FX widget */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Virtual USD account — gated on KYB activation. Until then we show
              a clear placeholder so nothing that looks like real bank details
              can leak out of an unverified account. */}
          <Card className="lg:col-span-3 border">
            <CardContent className="p-6 md:p-8">
              {u.kyb_status === "activated" ? (
                <>
                  <div className="flex items-center justify-between mb-3 gap-4">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Your USD collection account
                      </p>
                      <h2 className="font-display text-2xl font-bold mt-1">
                        Share these details with your overseas buyer to receive payment
                      </h2>
                    </div>
                    <Badge className="bg-primary text-primary-foreground rounded-full shrink-0">USD</Badge>
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="currency-chips">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-primary/30 bg-primary/10 text-primary">
                      <span className="h-1.5 w-1.5 rounded-full bg-success" /> USD · active
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-border text-muted-foreground bg-muted">
                      GBP · on request
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-border text-muted-foreground bg-muted">
                      EUR · on request
                    </span>
                  </div>

                  <div className="space-y-3">
                    <AccountRow label="Beneficiary" value={va.beneficiary_name} />
                    <AccountRow label="Account number" value={va.account_number} testId={DASHBOARD.copyAccountBtn} mono copyable />
                    <AccountRow label="Routing (ACH / ABA)" value={va.routing_number} testId={DASHBOARD.copyRoutingBtn} mono copyable />
                    <AccountRow label="SWIFT / BIC" value={va.swift_bic} testId={DASHBOARD.copySwiftBtn} mono copyable />
                    <AccountRow label="Bank" value={va.bank_name} />
                    <AccountRow label="Bank address" value={va.bank_address} />
                    <AccountRow label="Account type" value={va.account_type} />
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={copyAll}
                      variant="outline"
                      className="flex-1 h-12 text-sm font-semibold"
                      data-testid={DASHBOARD.copyAllBtn}
                    >
                      {copiedAll ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-success" /> All details copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" /> Copy all details
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={downloadPdf}
                      className="flex-1 h-12 text-sm font-semibold"
                      disabled={downloadingPdf}
                      data-testid={DASHBOARD.downloadPdfBtn}
                    >
                      {downloadingPdf ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing PDF…
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" /> Download as PDF
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="mt-4 text-xs text-muted-foreground">
                    Need to receive GBP or EUR from your buyers?{" "}
                    <Link to="/contact" className="text-primary font-semibold hover:underline" data-testid="activate-gbp-eur-link">
                      Contact us to activate
                    </Link>
                    .
                  </p>
                </>
              ) : (
                <div data-testid="usd-account-pending">
                  <div className="flex items-center justify-between mb-3 gap-4">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Your USD collection account
                      </p>
                      <h2 className="font-display text-2xl font-bold mt-1">
                        Complete verification to activate your USD account
                      </h2>
                    </div>
                    <Badge variant="outline" className="rounded-full shrink-0 border-[#C77F1E]/30 text-[#8F5A16] bg-[#FBF1E1]">
                      Pending KYB
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 max-w-md">
                    Your buyers&apos; payments will land here once your KYB is approved by our banking partner.
                    We&apos;ll email you the moment your account is live.
                  </p>
                  <div className="mt-6 rounded-lg border border-dashed border-border/70 bg-muted/40 p-6 space-y-3 text-sm text-muted-foreground">
                    <PendingRow label="Beneficiary" />
                    <PendingRow label="Account number" />
                    <PendingRow label="Routing (ACH / ABA)" />
                    <PendingRow label="SWIFT / BIC" />
                    <PendingRow label="Bank" />
                  </div>
                  <div className="mt-6">
                    <Link to="/kyb">
                      <Button className="h-12 px-6 text-sm font-semibold" data-testid="usd-account-start-kyb">
                        Complete verification →
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* FX widget */}
          <div className="lg:col-span-2" data-testid={DASHBOARD.fxWidget}>
            <Card className="border h-full">
              <CardContent className="p-6 md:p-8">
                <FxSavingsWidget refCode={u.referral_code} />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Payout bank account (INR) */}
        {payout && (
          <Card className="border" data-testid={DASHBOARD.payoutCard}>
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                    <Landmark className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Payout bank account
                    </p>
                    <p className="font-display text-2xl font-bold mt-1">
                      {payout.bank_name || "Your bank"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      <span>{payout.holder_name}</span>
                      <span className="mx-2">·</span>
                      <span className="sf-mono">{payout.number_masked}</span>
                      <span className="mx-2">·</span>
                      <span className="sf-mono">{payout.ifsc}</span>
                      <span className="mx-2">·</span>
                      <span>{payout.currency}</span>
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${payoutBadge.cls}`}
                  data-testid="payout-status-badge"
                >
                  {payoutBadge.label}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Referral link */}
        {u.referral_code && (
          <ReferralCard code={u.referral_code} />
        )}

        {/* Transactions */}
        <Card className="border">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Recent</p>
                <h2 className="font-display text-2xl font-bold mt-1">Payments received</h2>
              </div>
            </div>

            {transactions.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">No transactions yet.</div>
            ) : (
              <div className="divide-y">
                {transactions.map((t) => (
                  <div
                    key={t.id}
                    className="w-full flex items-center justify-between gap-4 py-4 px-3 -mx-3 group hover:bg-muted/40 rounded-lg transition-colors"
                    data-testid={DASHBOARD.txnRow}
                  >
                    <button
                      onClick={() => navigate(`/transactions/${t.id}`)}
                      className="flex items-center justify-between gap-4 flex-1 text-left min-w-0"
                      data-testid={`txn-open-${t.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">{t.sender_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(t.settlement_date).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}{" "}
                          · {t.sender_country_name || t.sender_country}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-display font-bold text-lg sf-mono text-primary">{usd(t.amount_usd)}</p>
                        <p className="text-sm text-muted-foreground sf-mono">{inr(t.amount_inr)}</p>
                      </div>
                    </button>
                    <div className="shrink-0 flex items-center gap-1">
                      {t.efira_available ? (
                        <TxnEfiraDownload txnId={t.id} />
                      ) : (
                        <span
                          className="text-xs text-muted-foreground/70 italic px-2"
                          title="eFIRA becomes available once the withdrawal is complete"
                          data-testid={`txn-efira-locked-${t.id}`}
                        >
                          eFIRA pending
                        </span>
                      )}
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function AccountRow({ label, value, mono, copyable, testId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b last:border-0">
      <span className="text-sm text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`text-base font-medium truncate ${mono ? "sf-mono" : ""}`}>{value}</span>
        {copyable && (
          <button
            onClick={doCopy}
            className="p-2 rounded-md hover:bg-muted transition-colors shrink-0"
            data-testid={testId}
            aria-label={`Copy ${label}`}
          >
            {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
          </button>
        )}
      </div>
    </div>
  );
}

function PendingRow({ label }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-muted-foreground/60 italic">Available after verification</span>
    </div>
  );
}

function TxnEfiraDownload({ txnId }) {
  const [busy, setBusy] = useState(false);
  const onClick = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${BACKEND_URL}/api/transactions/${txnId}/efira`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Flo-eFIRA-${txnId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("eFIRA downloaded");
    } catch (err) {
      toast.error(err?.message || "Could not download eFIRA");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
      data-testid={`txn-efira-download-${txnId}`}
      title="Download eFIRA (regulator-compliant remittance advice)"
    >
      {busy ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing…
        </>
      ) : (
        <>
          <Download className="h-3.5 w-3.5" /> eFIRA
        </>
      )}
    </button>
  );
}


function ReferralCard({ code }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = `${origin}/?ref=${code}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareLink = async () => {
    const text = "I use Flo Payments for USD → INR exports — better rates than my bank. Try it →";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Flo Payments", text, url: link });
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${link}`);
      toast.success("Referral link copied");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not share");
    }
  };

  return (
    <Card className="border" data-testid="referral-card">
      <CardContent className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
              <Gift className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Your referral link
              </p>
              <p className="font-display text-xl font-bold mt-1">Invite a fellow exporter</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Share this link with other exporters — we&apos;re building a reward program, coming soon.
              </p>
              <p
                className="text-sm sf-mono text-primary mt-3 break-all"
                data-testid="referral-link-text"
              >
                {link}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={copyLink}
              className="h-11 text-sm font-semibold justify-start"
              data-testid="referral-copy-btn"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2 text-success" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" /> Copy link
                </>
              )}
            </Button>
            <Button
              onClick={shareLink}
              className="h-11 text-sm font-semibold justify-start"
              data-testid="referral-share-btn"
            >
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
